import type { KubernetesMachineLiveStatusResource, KubernetesLiveStatusResource } from "@octopusdeploy/octopus-server-client";
import type { Dictionary } from "lodash";
import { useMemo } from "react";
import * as React from "react";
import KubernetesResourceTypeMultiSelect from "~/areas/projects/components/Observability/Filters/KubernetesResourceTypeMultiselect";
import { K8sResourceIcon, KnownKubernetesResources } from "~/areas/projects/components/Observability/KubernetesResources";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout/index";
import Select from "~/primitiveComponents/form/Select/Select";
import { KubernetesHealthStatusMultiSelect, KubernetesSyncStatusMultiSelect } from "./Filters/KubernetesStatusMultiselect";
export type LiveObjectStatusFilter = {
    kind: string[];
    namespace: string | undefined;
    healthStatus: string[];
    syncStatus: string[];
    name: string;
    showNamespacedResources: boolean;
    showClusterResources: boolean;
};
export type LiveStatusFilteringProps = {
    filter: LiveObjectStatusFilter;
    setFilter: (value: LiveObjectStatusFilter) => void;
    additionalKindTypes: string[];
    namespaceNames: string[];
};
export function filterMachineResources(input: KubernetesMachineLiveStatusResource[], filter: LiveObjectStatusFilter): KubernetesMachineLiveStatusResource[] {
    return input.map((m) => ({
        ...m,
        Resources: filterResources(m.Resources, filter),
    }));
}
export function filterResources(input: KubernetesLiveStatusResource[], filter: LiveObjectStatusFilter): KubernetesLiveStatusResource[] {
    return input
        .map((r: KubernetesLiveStatusResource) => {
        const matchingChildren = filterResources(r.Children, filter);
        if (matchingChildren.length > 0 || resourceMatchesFilters(r, filter)) {
            return {
                ...r,
                Children: matchingChildren,
            };
        }
        return undefined;
    })
        .filter((item) => item !== undefined);
}
function resourceMatchesFilters(input: KubernetesLiveStatusResource, resourceFilter: LiveObjectStatusFilter) {
    const shouldDisplayResource = fieldMatches(input.Name, resourceFilter.name) && fieldInRange(input.Kind, resourceFilter.kind) && fieldInRange(input.HealthStatus, resourceFilter.healthStatus) && fieldInRange(input.SyncStatus, resourceFilter.syncStatus);
    if (input.Namespace) {
        if (resourceFilter.showNamespacedResources) {
            return shouldDisplayResource && fieldMatches(input.Namespace, resourceFilter.namespace || "");
        }
        return false;
    }
    if (!resourceFilter.showClusterResources) {
        return false;
    }
    return shouldDisplayResource;
}
function fieldInRange(input: string | null, comparison?: string[]): boolean {
    return !comparison || comparison.length === 0 || (!!input && comparison.includes(input));
}
function fieldMatches(input: string, comparison: string): boolean {
    return comparison === "" || input.includes(comparison);
}
function createKindItems(additionalKinds: string[]): Dictionary<() => React.ReactElement> {
    const allKinds: Dictionary<() => React.ReactElement> = { ...KnownKubernetesResources };
    additionalKinds.forEach((kind) => {
        if (!allKinds[kind]) {
            allKinds[kind] = () => K8sResourceIcon({ resourceType: kind });
        }
    });
    return allKinds;
}
export function LiveStatusFiltering({ filter, setFilter, additionalKindTypes, namespaceNames }: LiveStatusFilteringProps) {
    const kindFilterItems = useMemo(() => createKindItems(additionalKindTypes), [additionalKindTypes]);
    const namespaceSelectOptions = namespaceNames.map((x) => ({ value: x, text: x }));
    return (<div>
            <KubernetesResourceTypeMultiSelect items={kindFilterItems} value={filter.kind} onChange={(kind) => setFilter({ ...filter, kind })}/>
            <Select key="namespaceFilter" value={filter.namespace} onChange={(namespace) => setFilter({ ...filter, namespace })} items={namespaceSelectOptions} allowClear={true} placeholder="By namespace"/>
            <AdvancedFilterCheckbox label="Show namespaced resources" value={filter.showNamespacedResources} onChange={(showNamespacedResources) => setFilter({ ...filter, showNamespacedResources })}/>
            <AdvancedFilterCheckbox label="Show cluster-level resources" value={filter.showClusterResources} onChange={(showClusterResources) => setFilter({ ...filter, showClusterResources })}/>
            <KubernetesHealthStatusMultiSelect value={filter.healthStatus} onChange={(status) => setFilter({ ...filter, healthStatus: status })}/>
            <KubernetesSyncStatusMultiSelect value={filter.syncStatus} onChange={(status) => setFilter({ ...filter, syncStatus: status })}/>
        </div>);
}
export const defaultFilter: LiveObjectStatusFilter = {
    kind: [],
    healthStatus: [],
    syncStatus: [],
    namespace: "",
    name: "",
    showNamespacedResources: true,
    showClusterResources: true,
};
